import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { CREDIT_API_URL } from "../constants";

const useChatStore = create(
    persist(
        (set, get) => ({
            customer: null, // Stores logged-in user details
            authToken: null, // Stores dynamic auth token for customer
            loanRequests: [], // Stores loan requests
            kycDocuments: [], // Stores KYC documents
            currentStep: 'LOGIN', // Tracks where the user is in the chat flow
            messages: [], // Chat messages
            chatHistory: [], // Stores chat messages
            loanId: null, // Store the active loan request ID
            chatSessionId: null, // Always track the active chat session
            isPanCompleted: false,
            isAadharCompleted: false,
            isGstCompleted: false,
            panNumber: null,
            aadharNumber: null,
            gstNumber: null,
            companypanNumber: null,

            // ✅ Set Customer & Check Active Chat Session on Login
            setCustomer: (customerObj) => {
                set({ customer: customerObj });
            },

            // ✅ Set Auth Token
            setAuthToken: (token) => {
                set({ authToken: token });
            },
           
            // Suppress automatic redirects to dashboard for short periods (timestamp ms)
            suppressAutoRedirectUntil: 0,

            // Set suppression for next durationMs milliseconds
            setSuppressAutoRedirect: (durationMs = 5000) => {
                set({ suppressAutoRedirectUntil: Date.now() + durationMs });
            },
            clearSuppressAutoRedirect: () => {
                set({ suppressAutoRedirectUntil: 0 });
            },

            // ✅ Fetch loan requests
            fetchLoans: async () => {
                const customer = get().customer;
                if (!customer) return;

                try {
                    const response = await axios.get(`/api/loans/${customer.customerId}`);
                    set({ loanRequests: response.data });
                } catch (error) {
                    console.error("Loan fetch failed:", error);
                }
            },

            // ✅ Fetch KYC documents
            fetchKYC: async () => {
                const customer = get().customer;
                if (!customer) return;

                try {
                    const response = await axios.get(`/api/kyc/${customer.customerId}`);
                    set({ kycDocuments: response.data });
                } catch (error) {
                    console.error("KYC fetch failed:", error);
                }
            },

            // ✅ Update chat messages
            addMessage: (sender, text) => {
                set((state) => ({
                    messages: [...state.messages, { sender, text }]
                }));
            },

            // ✅ Save Chat Message Linked to Chat Session
            addChatMessage: async (message, sender = "bot", kycDocumentId = null) => {
                const { customer, chatSessionId, loanId } = get();
                if (!chatSessionId) return; // No active session, don't save

                const newMessage = {
                    chatSessionId,
                    customerId: customer.customerId,
                    loanRequestId: loanId || null, // ✅ If a loan exists, attach it
                    sentBy: sender,
                    messageText: message.text,
                    responseType: message.responseType || "normal",
                    kycDocumentId,
                    attachmentPath: message.attachmentPath || null,
                };

                set((state) => ({
                    chatHistory: [...state.chatHistory, newMessage],
                }));

                // 🔥 Save to Database
                try {
                    await axios.post(`${CREDIT_API_URL}chat-history`, newMessage);
                } catch (error) {
                    console.error("❌ Chat save failed:", error);
                }
            },

            // ✅ Fetch Chat History for Active Chat Session
            fetchChatHistory: async (chatSessionId) => {
                try {
                    // Defensive: ensure chatSessionId is a string ID. Sometimes an object/array slips through.
                    let sessionId = chatSessionId;
                    if (!sessionId) {
                        console.warn('[chatStore] fetchChatHistory called with empty chatSessionId', chatSessionId);
                        return;
                    }
                    if (Array.isArray(sessionId)) {
                        // Try to extract a string id from the first entry
                        const first = sessionId[0];
                        if (typeof first === 'string') sessionId = first;
                        else if (first && (first.id || first.sessionId)) sessionId = first.id || first.sessionId;
                        else {
                            // fallback: attempt to stringify but it's likely invalid — bail out
                            console.warn('[chatStore] chatSessionId is array but contains no id strings', sessionId);
                            return;
                        }
                    } else if (typeof sessionId === 'object') {
                        // object case: extract id/sessionId if present
                        if (sessionId.id || sessionId.sessionId) {
                            sessionId = sessionId.id || sessionId.sessionId;
                        } else {
                            console.warn('[chatStore] chatSessionId is object but has no id/sessionId', sessionId);
                            return;
                        }
                    }

                    const response = await axios.get(`${CREDIT_API_URL}chat-history/session/${sessionId}`);
                    if (response.data && response.data.data.length > 0) {
                        set({ chatHistory: response.data.data });
                        const { loanId } = get();

                        // ✅ Detect Mobile Confirmation
                        const hasConfirmedMobile = response.data.data.some(chat => chat.messageText.includes("Mobile number confirmed"));

                        // ✅ Detect Loan Amount Updates
                        const lastLoanAmountMessage = response.data.data.findLast(chat => chat.messageText.includes("Requested Loan Amount: ₹"));
                        const hasConfirmedLoanAmount = !!lastLoanAmountMessage;

                        // ✅ Detect "No" Response to Proceeding
                        const userOptedNo = response.data.data.some(chat => chat.messageText.toLowerCase() === "no");

                        const updatedKyc = response.data.data.findLast(chat =>
                            chat.messageText.includes("Updated Application Type:")
                        );
                        
                        const initialKyc = response.data.data.findLast(chat =>
                            chat.messageText.includes("Selected Application Type:")
                        );
                        
                        const kycType = updatedKyc
                            ? updatedKyc.messageText.split(": ")[1]
                            : initialKyc
                            ? initialKyc.messageText.split(": ")[1]
                            : null;
                        
                        set({ kycType });

                        // ✅ Detect if PAN Number & PAN Upload exist in chat history
                        const hasPanNumber = response.data.data.findLast(chat => chat.messageText.includes("PAN Number:"));
                        const hasPanUpload = response.data.data.findLast(chat => chat.messageText.includes("PAN card document uploaded successfully!"));
                        const hasAadharNumber = response.data.data.findLast(chat => chat.messageText.includes("Aadhar Number:"));
                        const hasAadharUpload = response.data.data.some(chat => chat.messageText.includes("Aadhar card document uploaded successfully!"));
                        const hasGstNumber = response.data.data.some(chat => chat.messageText.includes("GST Number:"));
                        const hasGstUpload = response.data.data.some(chat => chat.messageText.includes("GST certificate uploaded successfully!"));
                        const hasCompanyPanNumber = response.data.data.some(chat => chat.messageText.includes("Company PAN Number:"));
                        const hasCompanyPanUpload = response.data.data.some(chat => chat.messageText.includes("Company PAN card document uploaded successfully!"));
                        const hasBankUpload = response.data.data.some(chat => chat.messageText.includes("Bank statement uploaded successfully!"));
                        const hasItrUpload = response.data.data.some(chat => chat.messageText.includes("ITR statement uploaded successfully!"));
                        const isEditingPan = response.data.data.some(chat => chat.messageText.includes("Updating PAN Number.."));
                        const hasEditingPanNumber = response.data.data.some(chat => chat.messageText.includes("PAN Number updated successfully"));
                        const isEditingUploadPan = response.data.data.some(chat => chat.messageText.includes("Re-upload PAN document requested."));
                        const hasEditingUploadPan = response.data.data.some(chat => chat.messageText.includes("New PAN card document updated successfully!"));
                        const isEditingAadhar = response.data.data.some(chat => chat.messageText.includes("Updating Aadhar Number.."));
                        const hasEditingAadharNumber = response.data.data.some(chat => chat.messageText.includes("Aadhar Number updated successfully"));
                        const isEditingUploadAadhar = response.data.data.some(chat => chat.messageText.includes("Re-upload Aadhar document requested."));
                        const hasEditingUploadAadhar = response.data.data.some(chat => chat.messageText.includes("New Aadhar card document updated successfully!"));
                        const isEditingUploadBankstmt = response.data.data.some(chat => chat.messageText.includes("Re-upload Bank Statement requested."));
                        const hasEditingUploadBankstmt = response.data.data.some(chat => chat.messageText.includes("New Bank statement document updated successfully!"));
                        const isEditingUploadItrstmt = response.data.data.some(chat => chat.messageText.includes("Re-upload ITR Statement requested."));
                        const hasEditingUploadItrstmt = response.data.data.some(chat => chat.messageText.includes("New ITR statement document updated successfully!"));
                        const isEditingGstNum = response.data.data.some(chat => chat.messageText.includes("Updating GST Number.."));
                        const hasEditingGstNum = response.data.data.some(chat => chat.messageText.includes("GST Number updated successfully!"));
                        const isEditingUploadGstcert = response.data.data.some(chat => chat.messageText.includes("Re-upload GST certificate requested."));
                        const hasEditingUploadGstcert = response.data.data.some(chat => chat.messageText.includes("New GST certificate document updated successfully!"));
                        const isEditingCompPan = response.data.data.some(chat => chat.messageText.includes("Updating Company PAN Number.."));
                        const hasEditingCompPan = response.data.data.some(chat => chat.messageText.includes("Company PAN Number updated successfully!"));
                        const isEditingUploadCompPan = response.data.data.some(chat => chat.messageText.includes("Re-upload Company PAN card document requested."));
                        const hasEditingUploadCompPan = response.data.data.some(chat => chat.messageText.includes("New Company PAN card document updated successfully!"));

                        const editingGstMsg = response.data.data.findLast(chat =>
                            chat.messageText.includes("Updating GST Number..."));

                        const updatedGstMsg = response.data.data.findLast(chat =>
                            chat.messageText.includes("GST Number updated successfully!"));

                        const isPendingGstEdit = editingGstMsg && (!updatedGstMsg || new Date(updatedGstMsg.createdAt) < new Date(editingGstMsg.createdAt));

                        // ✅ Store in Zustand so UI knows to skip PAN steps
                        set({ isPanCompleted: hasPanNumber && hasPanUpload });
                        set({ isAadharCompleted: hasAadharNumber && hasAadharUpload });
                        set({ isGstCompleted: hasGstNumber && hasGstUpload });
                        set({ isBankCompleted: hasBankUpload });
                        set({ isItrCompleted: hasItrUpload });

                        // Always set panNumber from chat history if present
                        if (hasPanNumber) {
                          const pan = hasPanNumber.messageText.split("PAN Number:")[1]?.trim();
                          set({ panNumber: pan });
                        } else {
                          set({ panNumber: null });
                        }
                        // Always set aadharNumber from chat history if present
                        if (hasAadharNumber) {
                          const aadhar = hasAadharNumber.messageText.split("Aadhar Number:")[1]?.trim();
                          set({ aadharNumber: aadhar });
                        } else {
                          set({ aadharNumber: null });
                        }

                        if (loanId) {
                            // ✅ Determine Current Step Based on KYC Type
                            if (!kycType) {
                                set({ currentStep: "APPLICANT_TYPE" }); // Ask Individual or Company
                            } else if (kycType === "Individual") {
                                if (isEditingPan && !hasEditingPanNumber) {
                                    set({ currentStep: "EDIT_PAN" }); // Move to Edit PAN if not done
                                } else if (isEditingUploadPan && !hasEditingUploadPan) {
                                    set({ currentStep: "EDIT_PAN_UPLOAD" }); // Start with PAN if not done
                                } else if (!hasPanNumber || !hasPanUpload) {
                                    set({ currentStep: "KYC_START" }); // Start with PAN if not done
                                } else if (isEditingAadhar && !hasEditingAadharNumber) {
                                    set({ currentStep: "EDIT_Aadhar" });
                                } else if (isEditingUploadAadhar && !hasEditingUploadAadhar) {
                                    set({ currentStep: "EDIT_Aadhar_UPLOAD" });
                                } else if (!hasAadharNumber || !hasAadharUpload) {
                                    set({ currentStep: "AADHAR_START" }); // Move to Aadhar if not done
                                } else if (isEditingUploadBankstmt && !hasEditingUploadBankstmt) {
                                    set({ currentStep: "EDIT_BANK_UPLOAD" });
                                } else if (!hasBankUpload) {
                                    set({ currentStep: "BANK_UPLOAD" }); // Move to Bank Statement Upload
                                } else if (isEditingUploadItrstmt && !hasEditingUploadItrstmt) {
                                    set({ currentStep: "EDIT_ITR_UPLOAD" });
                                } else if (!hasItrUpload) {
                                    set({ currentStep: "ITR_UPLOAD" }); // Move to ITR Upload
                                } else {
                                    set({ currentStep: "THANK_YOU" }); // ✅ Final step - Chat completed
                                }
                            } else if (kycType === "Company") {
                                if (isPendingGstEdit) {
                                    set({ currentStep: "EDIT_GST" });
                                } else if (isEditingUploadGstcert && !hasEditingUploadGstcert) {
                                    set({ currentStep: "EDIT_GST_UPLOAD" });
                                } else if (!hasGstNumber || !hasGstUpload) {
                                    set({ currentStep: "COMPANY_GST_START" }); // Start with GST if not done
                                } else if (isEditingCompPan && !hasEditingCompPan) {
                                    set({ currentStep: "EDIT_COMP_PAN" }); // Start with GST if not done
                                } else if (isEditingUploadCompPan && !hasEditingUploadCompPan) {
                                    set({ currentStep: "EDIT_CMP_PAN_UPLOAD" }); // Start with GST if not done
                                } else if (!hasCompanyPanNumber || !hasCompanyPanUpload) {
                                    set({ currentStep: "COMPNAY_PAN_START" }); // Move to Company PAN if not done
                                } else if (!hasBankUpload) {
                                    set({ currentStep: "BANK_UPLOAD" }); // Move to Bank Statement Upload
                                } else if (!hasItrUpload) {
                                    set({ currentStep: "ITR_UPLOAD" }); // Move to ITR Upload
                                } else {
                                    set({ currentStep: "THANK_YOU" }); // ✅ Final step - Chat completed
                                }
                            }
                        } else {
                            if (userOptedNo && !hasConfirmedLoanAmount) {
                                set({ currentStep: "LOAN_AMOUNT" }); // ✅ Ask for Loan Amount Only If "No" was Selected
                            } 
                            // else if (!hasConfirmedMobile) {
                            //     set({ currentStep: "MOBILE_CONFIRM" }); // ✅ Start with Mobile Confirmation
                            // } 
                            else {
                                set({ currentStep: "WELCOME" });
                            }
                        }
                    } else {
                        await get().addChatMessage({ text: "Hey there! 👋 I'm Bizzie, your loan buddy!" });
                        set({ currentStep: "WELCOME" });
                    }
                } catch (error) {
                    console.error("❌ Fetching chat history failed:", error);
                }
            },

            resetChat: () => {
                set({
                    chatSessionId: null,
                    loanId: null,
                    chatHistory: [],
                });
            },

            logout: () => {
                set({
                    customer: null,
                    authToken: null,
                    chatSessionId: null,
                    loanId: null,
                    chatHistory: [],
                });
            },

            clearChatSession: () => {
                set({
                    customer: null,
                    chatSessionId: null,
                    loanId: null,
                    chatHistory: [],
                    currentStep: "MOBILE_CONFIRM",
                    isPanCompleted: false,
                    isAadharCompleted: false,
                    isBankCompleted: false,
                    isItrCompleted: false,
                    isGstCompleted: false,
                    kycType: null,
                    panNumber: null,
                    aadharNumber: null,
                    gstNumber: null,
                    companypanNumber: null,
                });
            },

            setPanNumber: (value) => set({ panNumber: value }),
            setAadharNumber: (value) => set({ aadharNumber: value }),
            setGstNumber: (value) => set({ gstNumber: value }),
            setCompanypanNumber: (value) => set({ companypanNumber: value }),

            setCurrentStep: (step) => set({ currentStep: step }),

        }),
        { name: "chat-storage" } // Enables persistence
    )
);

export default useChatStore;
